
import { deckStore, generalStore, userStore } from "@/store";
import { deckService } from "@/services/deck.service";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { queue, socket, useNumOnline } from "../services/game.service";
import router from "@/router";
import { userService } from "@/services/user.service";
import { onBeforeRouteLeave } from "vue-router";
import { useToast } from "vue-toastification";
import { GameRecord } from "@/types/gameRecord";
import ImageLoader from "@/components/UI/ImageLoader.vue";
import Toggle from "@/components/UI/Toggle.vue";
import Tabs from "@/components/UI/Tabs.vue";

export default defineComponent({
  name: "Dashboard",
  components: { ImageLoader, Toggle, Tabs },

  setup() {
    const toast = useToast();
    const selectedDeckId = ref<number>();
    const decks = computed(deckStore.getters.decks);
    const user = computed(userStore.getters.user);

    // not reactive - get value and unset so the dashboard only displays it once
    const numDecksRemoved = ref(generalStore.getters.numDecksRemoved());
    const decksAltered = ref(generalStore.getters.decksAltered());
    onBeforeRouteLeave(() => {
      generalStore.mutations.setNumDecksRemoved(0);
      generalStore.mutations.setDecksAltered(false);
    });

    userService.setStoreUser();
    const { numOnline, getNumOnline } = useNumOnline();
    getNumOnline();

    onMounted(async () => {
      await deckService.updateStoreDecks();
      if (decks.value.length > 0) selectedDeckId.value = decks.value[0].id;
    });
    const inQueue = ref<boolean>(false);

    const setAddress = async () => {
      userService.syncBanMonkeyconnect();
    };

    const logout = async () => {
      await userService.logout();
      userStore.mutations.logout();
      router.push("/");
    };

    const customQueueName = ref("");

    const playTabs = ref([{ text: "Online" }, { text: "Against Computer" }]);
    const playIndex = ref(0);

    const singlePlayer = () => {
      // exit queue
      socket.disconnect();
      // open single player
      router.push({
        name: "GameView",
        query: {
          ai: "true",
          deckId: selectedDeckId.value,
        },
      });
    };

    const notify = ref(false);
    watch(
      () => notify.value,
      async (val) => {
        if (!val) return;
        if (!("Notification" in window)) {
          toast.error("Your browser does not support notifications!");
        } else if (Notification.permission === "granted") return;
        else {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") {
            toast.error(
              "Notification permission was denied. You can enable them for this site from your browser settings"
            );
            notify.value = false;
          }
        }
      }
    );

    const joinQueue = () => {
      if (!selectedDeckId.value) return;
      inQueue.value = true;
      queue(
        selectedDeckId.value,
        customQueueName.value,
        () => {
          if (Notification.permission === "granted" && notify.value)
            new Notification("Game found");
          router.push({ name: "GameView" });
        },
        (res) => {
          inQueue.value = false;
          toast.error(res.error || "There was an error");
          console.log(res.error);
        }
      );
    };

    const recentGames = ref<GameRecord[]>([]);
    const loadRecentGames = async () => {
      const result = await userService.getRecentGames();
      recentGames.value.push(...result);
    };
    loadRecentGames();

    return {
      decks,
      joinQueue,
      notify,
      playIndex,
      playTabs,
      singlePlayer,
      inQueue,
      selectedDeckId,
      user,
      setAddress,
      numOnline,
      customQueueName,
      logout,
      numDecksRemoved,
      decksAltered,
      recentGames,
    };
  },
});
